import { GeminiLiveService } from '@/services/geminiLiveService'
import { handleError, SessionError } from '@/lib/errors'
import { logger } from '@/lib/logging'
import { shouldHideModelMessage } from './format'
import { startRecording } from './startRecording'
import { buildTurnMessages, trackQuestionProgress } from './turnTracking'
import type { SessionRefs, SessionSetters } from './types'
import type { PromptConfig } from '@bosmart/types'

interface CreateLiveServiceArgs {
  refs: SessionRefs
  setters: SessionSetters
  prompt?: PromptConfig
  liveToken: string
  interviewId: string
  apiBaseUrl: string
  scheduleReconnect: (reason: string) => void
  showNotification: (message: string) => void
  startVideoStreaming: () => void
}

export function createLiveService({
  refs,
  setters,
  prompt,
  liveToken,
  interviewId,
  apiBaseUrl,
  scheduleReconnect,
  showNotification,
  startVideoStreaming,
}: CreateLiveServiceArgs) {
  return new GeminiLiveService({
    apiKey: liveToken,
    prompt,
    onOpen: () => {
      logger.info('Gemini Live service connected')
      refs.reconnectBackoffRef.current = 2000
      refs.reconnectAttemptsRef.current = 0
      refs.shouldReconnectRef.current = true
      startVideoStreaming()
      setters.setIsTimerRunning(true)
      startRecording({ refs, interviewId, apiBaseUrl })
    },
    onClose: () => {
      logger.info('Gemini Live service disconnected')
      setters.setIsTimerRunning(false)
      if (!refs.isIntentionallyDisconnectingRef.current && !refs.isConnectingRef.current) {
        scheduleReconnect('socket-close')
      } else {
        logger.info('Skipping reconnect - disconnect was intentional or connection in progress')
      }
    },
    onError: () => {
      const sessionError = new SessionError(
        'Connection interrupted. Reconnecting...',
        'SESSION_CONNECTION_ERROR'
      )
      handleError(sessionError, 'InterviewSession:GeminiLiveService')
      showNotification('Connection interrupted. Reconnecting...')
      setters.setIsTimerRunning(false)
      scheduleReconnect('session-error')
    },
    onAudioData: setters.setAiSpeaking,
    onTranscription: (user, model) => {
      refs.lastActivityRef.current = Date.now()
      setters.setTranscription({
        user,
        model: shouldHideModelMessage(model) ? '' : model,
      })
    },
    onTurnComplete: (userText, modelText) => {
      const sanitizedModel = shouldHideModelMessage(modelText) ? '' : modelText
      setters.setHistory((history) => [...history, ...buildTurnMessages(userText, sanitizedModel)])
      trackQuestionProgress(refs, userText, sanitizedModel)
    },
  })
}
